/**
 * "Conhecendo seu negócio" (docs/plano-perfil-do-negocio-onboarding-v1.md): tipo de comida principal e faixa de
 * faturamento do delivery no mês passado. Listas fechadas, compartilhadas com o painel. Funções puras.
 */
export class ErroPerfilDoNegocio extends Error {}

export interface TipoDeComida { id: string; nome: string; icone: string; }
export interface FaixaDeFaturamento { id: string; nome: string; }

/** Neutra de segmento; a ordem é a dos chips. */
export const TIPOS_DE_COMIDA: TipoDeComida[] = [
  {id: 'LANCHES', nome: 'Hambúrguer e lanches', icone: 'fas fa-hamburger'},
  {id: 'PIZZA', nome: 'Pizza', icone: 'fas fa-pizza-slice'},
  {id: 'ACAI', nome: 'Açaí e sorvete', icone: 'fas fa-ice-cream'},
  {id: 'MARMITA', nome: 'Marmita e prato feito', icone: 'fas fa-utensils'},
  {id: 'ORIENTAL', nome: 'Japonesa e oriental', icone: 'fas fa-fish'},
  {id: 'PASTEL', nome: 'Pastel e salgados', icone: 'fas fa-cookie'},
  {id: 'DOCES', nome: 'Doces e bolos', icone: 'fas fa-birthday-cake'},
  {id: 'PADARIA', nome: 'Padaria e café', icone: 'fas fa-coffee'},
  {id: 'SAUDAVEL', nome: 'Saudável', icone: 'fas fa-leaf'},
  {id: 'ITALIANA', nome: 'Italiana', icone: 'fas fa-wine-glass-alt'},
  {id: 'ARABE', nome: 'Árabe', icone: 'fas fa-moon'},
  {id: 'FRUTOS_DO_MAR', nome: 'Frutos do mar', icone: 'fas fa-water'},
  {id: 'BEBIDAS', nome: 'Bebidas e adega', icone: 'fas fa-glass-cheers'},
  {id: 'OUTRO', nome: 'Outro', icone: 'fas fa-question-circle'}
];

export const FAIXAS_DE_FATURAMENTO: FaixaDeFaturamento[] = [
  {id: 'ATE_5', nome: 'Até R$ 5 mil'},
  {id: 'DE_5_A_15', nome: 'R$ 5 a 15 mil'},
  {id: 'DE_15_A_40', nome: 'R$ 15 a 40 mil'},
  {id: 'DE_40_A_100', nome: 'R$ 40 a 100 mil'},
  {id: 'MAIS_DE_100', nome: 'Mais de R$ 100 mil'},
  {id: 'NAO_VENDE', nome: 'Ainda não vendo delivery'}
];

export interface PerfilDoNegocio { tipoComida: string; tipoComidaOutro: string | null; faixaFaturamento: string; }

/** A primeira que casar vence ("pizzaria e esfiharia" é pizza). */
const PALAVRAS_DO_TIPO: Array<[string, RegExp]> = [
  ['PIZZA', /pizz/],
  ['LANCHES', /hamb|burg|lanch|hot ?dog|sanduich/],
  ['ACAI', /acai|sorve|gelat/],
  ['ORIENTAL', /japon|sushi|temaki|poke|chines|orient/],
  ['MARMITA', /marmit|prato feito|self|almoc|executiv|comida caseira/],
  ['ARABE', /arabe|esfi|kebab|shawarma/],
  ['PASTEL', /pastel|salgad|coxinha/],
  ['DOCES', /doce|confeit|bolo|brigadeir|torta/],
  ['PADARIA', /padar|panific|\bcafe/],
  ['SAUDAVEL', /saudav|\bfit\b|salada|natural/],
  ['ITALIANA', /italian|massa|lasanh/],
  ['FRUTOS_DO_MAR', /frutos do mar|peixe|camar/],
  ['BEBIDAS', /adega|bebida|conveni|cervej|chopp/]
];

function semAcento(texto: any): string {
  return String(texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Tipo pré-marcado pelo que a Mia anotou na conversa e, sem isso, pelo segmento da empresa. */
export function tipoSugerido(segmentoDaConversa: any, segmentoEmpresa: any): string | null {
  for (const texto of [segmentoDaConversa, segmentoEmpresa]) {
    const t = semAcento(texto);
    if (!t) continue;
    const achado = PALAVRAS_DO_TIPO.find(([, palavras]) => palavras.test(t));
    if (achado) return achado[0];
  }
  return null;
}

/** O texto que descreve o tipo (o nome, ou o que o dono escreveu em Outro), para o horário padrão. */
export function textoDoTipo(tipoComida: string | null | undefined, tipoComidaOutro?: string | null): string | null {
  if (!tipoComida) return null;
  if (tipoComida === 'OUTRO') return tipoComidaOutro || null;
  return TIPOS_DE_COMIDA.find(t => t.id === tipoComida)?.nome || null;
}

export function nomeDaFaixa(faixa: string | null | undefined): string | null {
  return FAIXAS_DE_FATURAMENTO.find(f => f.id === faixa)?.nome || null;
}

export function validePerfil(dados: any): PerfilDoNegocio {
  const tipoComida = String(dados?.tipoComida || '');
  if (!TIPOS_DE_COMIDA.some(t => t.id === tipoComida)) throw new ErroPerfilDoNegocio('Escolha o tipo de comida da loja.');
  const outro = String(dados?.tipoComidaOutro || '').trim().slice(0, 80);
  if (tipoComida === 'OUTRO' && outro.length < 2) throw new ErroPerfilDoNegocio('Conte qual é o tipo de comida da loja.');
  const faixaFaturamento = String(dados?.faixaFaturamento || '');
  if (!FAIXAS_DE_FATURAMENTO.some(f => f.id === faixaFaturamento)) throw new ErroPerfilDoNegocio('Escolha quanto seu delivery vendeu no mês passado.');
  return {tipoComida, tipoComidaOutro: tipoComida === 'OUTRO' ? outro : null, faixaFaturamento};
}
